import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { User } from '@supabase/supabase-js';
import { toast } from 'react-hot-toast';
import { clearSessionData } from '../lib/supabase';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  error: null,
  refreshSession: async () => {}
});

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshSession = async () => {
    try {
      const { data: { session }, error } = await supabase().auth.refreshSession();
      if (error) throw error;
      setUser(session?.user ?? null);
      setError(null);
    } catch (err) {
      console.error('Error al refrescar la sesión:', err);
      if (err instanceof Error && err.message.includes('Invalid Refresh Token')) {
        // Limpiar el estado y redirigir al login
        setUser(null);
        setError('Sesión expirada. Por favor, inicia sesión nuevamente.');
        void clearSessionData();
        toast.error('Sesión expirada. Por favor, inicia sesión nuevamente.');
      } else {
        setError(err instanceof Error ? err.message : 'Error de autenticación');
      }
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { session }, error } = await supabase().auth.getSession();
        if (error) throw error;
        setUser(session?.user ?? null);
        setError(null);
      } catch (err) {
        console.error('Error en checkAuth:', err);
        if (err instanceof Error && err.message.includes('Invalid Refresh Token')) {
          setUser(null);
          setError('Sesión expirada. Por favor, inicia sesión nuevamente.');
          void clearSessionData();
          toast.error('Sesión expirada. Por favor, inicia sesión nuevamente.');
        } else {
          setError(err instanceof Error ? err.message : 'Error de autenticación');
        }
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, error, refreshSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 